<template>
    <!-- 
    quicktable-filters | Quicktable filters
    
    @Requiere 
    + ui-input-handler@1.21.0 o superior
    + ui-edit-auto-modal.vue@1.18.0 o superior
    + quicktable-select@1.4.0 o superior

    Ahora permite trabajar con Rangos de fechas, además se hizo una optimización profunda en la carga de
    valores por defecto y la organización de visualización de filtros de tipo fecha y rango de fecha.

    Permite que el v-b-tooltip.hover funcione correctamente se pueda desactivar con el parámetro disableBFilter

    @version 2.2.1
    @author Daniel Lepe
    @date 13 de Noviembre, 2025
    

    @version 2.2.0
    @author Daniel Lepe
    @date 10 de Febrero, 2025
    
    @version 2.1.3
    @author Daniel Lepe
    @date 24 de Enero, 2025
    Fix for autoload 'today' in filter dates.
    
    @version 2.1.2
    @author Daniel Lepe
    @date 24 de Enero, 2025
    Usa tiempo local para 'today'
    
    @version 2.1.1
    @author Daniel Lepe
    @date 25 de Enero, 2025
    Reparación de b-form-input[type=date]
    
    @version 2.0
    @author Daniel Lepe
    @date 24 de Enero, 2025
    Integración completa con ui-grouping-definitino
    
    @version 1.1
    @author Daniel Lepe
    @date 16 de Enero, 2025
    Permite filtrar con default para fecha en formato default: ['YYYY-MM-DD'|'today'], además se agregó el botón 'Hoy'

    Pe:
    {
        key: 'FechaFija',
        type: 'date',
        default: '2025-01-16'
    },
    {
        key: 'FechaHoy',
        type: 'date',
        default: 'today'
    }
    
    @version 1.0
    @author Daniel Lepe
    @date 25 de Sept, 2024
    Permite su inclusión en listas generales.
    -->
    <div class="quicktable-filters" :key="md5FromGroupingDefinitions">


        <ui-grouping-definition v-b-tooltip.hover :disabled="disableBFilter" v-for="item in cFiltersOverlimited" 
            v-model="value[item.key]"
            :key="item.key + '-grouping-definition'" 
            :class="item.key" 
            :label="getLabel(item)"
            :placeholder="getPlaceholderLabel(item)" 
            :title="getTitle(item)" 
            :options="item.options" 
            :kIndex="item.key"
            apply-button="Filter" 
            disableAdding 
            @changed="$emit('changed')"/>

        <b-button-group v-b-tooltip.hover :disabled="disableBFilter" v-for="item in cFiltersDates"
            :key="item.key + 'menu-card' + md5FromGroupingDefinitions" 
            :class="item.key" 
            :title="getTitle(item)">

            <b-input-group v-if="item.type == 'daterange'" class="fix-daterange-width">

                <b-form-datepicker @input="setDateRangeAt($event, item.key, 'start');" :value="value[item.key].start"
                    :max="value[item.key].end" :date-format-options="dateFormatOptions" />

                <b-form-datepicker @input="setDateRangeAt($event, item.key, 'end');" :value="value[item.key].end"
                    :date-format-options="dateFormatOptions" :min="value[item.key].start" />

            </b-input-group>

            <template v-else-if="item.type == 'date'">
                <b-button @click="setToday(item.key)" variant="info">
                    <i class="fa fa-circle"></i>
                </b-button>
                <b-form-input :value="value[item.key]" :key="'oI-' + item.key" type="date"
                    @change="check(item.key, $event)" />
            </template>

        </b-button-group>

        <b-button-group v-b-tooltip.hover :disabled="disableBFilter" v-for="item in cFiltersUnderlimited"
            :key="item.key + 'menu-card' + md5FromGroupingDefinitions" :class="item.key" :title="getTitle(item)">

            <template v-if="item.type == 'switch'">
                <b-form>
                    <b-form-checkbox class="pr-2" :value="isActive(item.key, opt)" switch switch-size="lg"
                        @click="switchItem(item, $event)">
                        {{ isActive(item.key, opt) ? item.yesOption : item.noOption }}
                    </b-form-checkbox>
                </b-form>
            </template>

            <template v-else-if="('options' in item) && item.options.length">
                <b-button v-for="(opt, $oI) in item.options" :key="'oI-' + $oI"
                    :variant="(!isActive(item.key, opt) ? 'info' : 'secondary')" 
                    @click="activate(item.key, opt)">
                    <span v-if="typeof opt == 'object' && opt.label"> {{ opt.label }}</span>
                    <span v-else> {{ opt }}</span>
                </b-button>
            </template>

            <b-button v-else variant="info" disabled>
                {{ emptyOptions }}
            </b-button>

        </b-button-group>

    </div>
</template>

<script>
import _ from 'lodash'
import md5 from 'js-md5';
import UiGroupingDefinition from './ui-grouping-definition'
import { toHumanReadable, notify } from '@/utils.js'
import moment from 'moment'

function getToday() {
    return moment().format('YYYY-MM-DD');
}

export default {
    components: { UiGroupingDefinition },
    name: 'quicktableFilters',
    props: {
        args: {
            type: Object,
            default: () => { return {} }
        },
        disableBFilter: {
            type: Boolean,
            default: false
        },
        dateFormatOptions: {
            type: Object,
            default: () => {
                return {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric'
                }
            }
        },
        emptyOptions: {
            type: String,
            default: 'No options to filter with'
        },
        filterByLabel: {
            type: String,
            default: 'Filtrar por: '
        },
        value: {
            type: Object,
        },
        maxInline: {
            type: Number,
            default: 5
        },
        debug: {
            type: Boolean,
            default: true
        },
        groupingDefinitions: {
            type: Object,
            default: () => {
                return {}
            }
        },
        filters: {
            type: [Object, Function]
        }
    },
    data: () => {
        return {
            minDate: new Date().toISOString().slice(0, 10)
        }
    },
    computed: {
        md5FromGroupingDefinitions(){
            let obj = (_.isObjectLike(this.groupingDefinitions) && this.groupingDefinitions !== null) ? this.groupingDefinitions : {};
            console.log('md5FromGroupingDefinitions', obj, JSON.stringify(obj))
            return md5(JSON.stringify(obj));
        },
        cFilters() {
            let r = [];
            try {
                if (_.isFunction(this.filters)) {
                    r = this.filters();
                } else if (_.isArray(this.filters)) {
                    r = _.cloneDeep(this.filters)
                }
                _.map(r, item => {
                    if((_.isObjectLike(this.groupingDefinitions) && this.groupingDefinitions !== null) && (item.key in this.groupingDefinitions))
                        item.options = this.groupingDefinitions[item.key];
                    if(item.key in this.args ){
                        item.required = true;
                        item.options = [this.args[item.key]];
                    }
                    return item;
                })
            } catch (e) {
                console.error(e)
            }
            return r;
        },
        cFiltersOverlimited() {
            return _.filter(this.cFilters, (item) => {
                return ((!('type' in item) || ['date', 'daterange'].indexOf(item.type) == -1) && (('options' in item) && item.options.length > this.maxInline));
            })
        },
        cFiltersUnderlimited() {
            return _.filter(this.cFilters, (item) => {
                return ((!('type' in item) || ['date', 'daterange'].indexOf(item.type) == -1) && (('options' in item) && item.options.length <= this.maxInline));
            })
        },
        cFiltersDates() {
            return _.filter(this.cFilters, (item) => {
                return (('type' in item) && (['date', 'daterange'].indexOf(item.type) !== -1));
            })
        }
    },
    methods: {
        setDateRangeAt(fecha, itemKey, mode) {
            try {
                let clonedValue = _.cloneDeep(this.value);
                clonedValue[itemKey][mode] = fecha;
                this.$emit('input', clonedValue);
                this.$emit('changed');
            } catch (e) {
                console.error(e)
            }
        },
        reviewDefaultFilterValues() {
            try {
                let preValue = _.clone(this.value)
                if (!_.isObjectLike(preValue))
                    preValue = {};
                _.forEach(this.cFilters, item => {
                    let preValueItem = (('default' in item && item.default) ? item.default : null);
                    if (('type' in item) && item.type == 'date' && item.default == 'today'){
                        preValueItem = getToday();
                    } else if (('type' in item) && item.type == 'daterange'){
                        preValueItem = {
                            start: getToday(),
                            end: getToday()
                        };
                    } else if (('required' in item) && item.required){
                        console.log('reviewDefaultFilterValues', item)
                        let defaultItem = item.options[0];
                        preValueItem = (_.isObjectLike(defaultItem) && 'key' in defaultItem)? defaultItem.key : defaultItem;
                    }
                    preValue[item.key] = preValueItem;
                })
                this.$emit('input', preValue)
            } catch (e) {
                notify(e)
                this.$emit('input', {})
            }
            return;
        },
        switchItem(item, $event) {
            console.log('switchItem', item, $event)
        },
        setToday(itemKey) {
            // const today = new Date().toJSON().slice(0,10); // Uses local machine time
            // const today = new Date().toISOString().split('T')[0]; // Uses UTC
            this.activate(itemKey, getToday());
        },
        check(itemKey, value) {
            this.activate(itemKey, value)
        },
        getTitle(item) {
            return (this.filterByLabel + this.$options.filters.translate(this.getLabel(item)))
        },
        getPlaceholderLabel(item) {
            let label = toHumanReadable(item.key);
            if (typeof item.label == String)
                label = item.label
            return 'Filter by ' + label;
        },
        getLabel(item) {
            if (typeof item.label == String)
                return item.label
            else
                return toHumanReadable(item.key)
        },
        getOptKey(opt) {
            console.log('getOptKey', opt)
            return ((typeof opt == 'object') ? opt.key : opt);
        },
        activate(itemKey, opt) {
            try {
                let fullObject = _.find(this.cFilters, { key: itemKey }),
                    cp = _.cloneDeep(this.value);

                // REVISA QUE THIS VALUE YA SEA UN OBJECTO
                if (typeof this.value != 'object')
                    this.$emit('input', { itemKey: this.getOptKey(opt) });

                if (cp[itemKey] == this.getOptKey(opt) && !fullObject.required) {
                    console.log('this.cFilters[itemKey].required', this.cFilters[itemKey], itemKey)
                    delete cp[itemKey];
                }
                else {
                    cp[itemKey] = this.getOptKey(opt);
                }
                this.$emit('input', cp)
                this.$emit('changed');
            } catch (e) {
                console.error(e)
            }
        },
        getValue(itemKey) {
            let ret = '';
            try {
                // REVISA QUE THIS VALUE YA SEA UN OBJECTO
                if (typeof this.value != 'object')
                    throw 'Value is not object';

                // REVISA LA CONFIGURACIÓN DE OPT
                if (typeof this.value[itemKey] == String)
                    ret = this.value[itemKey];
            } catch (e) {
                console.error(e)
            }
            return ret;
        },
        isActive(itemKey, opt) {
            let ret = false;
            try {
                // REVISA QUE THIS VALUE YA SEA UN OBJECTO
                if (typeof this.value != 'object')
                    throw 'Value is not object';

                // REVISA LA CONFIGURACIÓN DE OPT
                if (this.value[itemKey] == this.getOptKey(opt))
                    ret = true;
            } catch (e) {
                console.error(e)
            }
            return ret;
        },
        reviewDateDefaults() {
            const filters = _.cloneDeep(this.cFilters);
            console.log('reviewDateDefaults.filters', filters)
            _.forEach(filters, item => {
                console.log('reviewDateDefaults.item', item)
                if (item.type == 'date' && item.default == 'today')
                    this.setToday(item.key)
                else if (item.type == 'date' && item.default)
                    this.activate(item.key, item.default);
            })
        }
    },
    beforeMount() {
        this.reviewDefaultFilterValues()
    },
    mounted() {
        this.$nextTick(this.reviewDateDefaults);
    }
}

</script>

<style lang="scss" scoped>
.quicktable-filters {

    display: flex;
    flex-flow: row;
    gap: 1em;

    .fix-daterange-width {
        min-width: 20em;
    }

    .menu-card {
        max-width: 300px;
    }

    .ml-3 {
        margin-left: 1rem;
    }

    @media print {
        // #CocinareporteinterrelacionQuicktable {
            .btn-info {
                display: none !important;
            }
        // }
    }
}

</style>